import { SensorType } from 'common';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Text, XStack, YStack, Spinner } from 'tamagui';
import { tokens } from 'tamagui-config/src/tokens';

import { HubLineChart } from '../../../components/common/HubLineChart';
import { useAppSync } from '../../../contexts/AppSyncContext';
import { useHumidity } from '../../../hooks/water/useHumidity';
import { getHumidityColor, getHumidityGradient } from '../../../utils/humidityUtils';

export const HumidityContent: React.FC = () => {
  const { t } = useTranslation();
  const { sensors, selectedHubId } = useAppSync();

  const leakDetectorSensor = sensors.find(
    (sensor) => sensor.hubId === selectedHubId && sensor.type === SensorType.LEAK_DETECTOR,
  );

  const { humidityData, loading, error, latestHumidityValue, humidityStatus } = useHumidity(
    leakDetectorSensor?.id || '',
  );

  if (loading) {
    return (
      <YStack height={140} justifyContent="center" alignItems="center">
        <Spinner size="large" color={tokens.color.white12} />
      </YStack>
    );
  }

  if (error || !humidityData.length) {
    return (
      <YStack height={140} justifyContent="center" alignItems="center">
        <Text color={tokens.color.white12}>{error || 'No humidity data available'}</Text>
      </YStack>
    );
  }

  return (
    <YStack gap="$5">
      <YStack>
        <XStack alignItems="center" gap="$4">
          <Text fontFamily="$heading" fontWeight="$9.5" fontSize="$9.5">
            {latestHumidityValue ? `${Math.round(Math.min(latestHumidityValue, 100))}%` : '--'}
          </Text>
          <Text fontSize="$11">{t('water.humidity')}</Text>
        </XStack>

        {humidityStatus && (
          <Text fontSize="$6" fontWeight="$5" color={tokens.color.textSecondary}>
            {humidityStatus}
          </Text>
        )}
      </YStack>

      <HubLineChart
        data={humidityData}
        unit={'%'}
        minValue={0}
        maxValue={100}
        chartHeight={105}
        noOfSections={2}
        color={latestHumidityValue ? getHumidityColor(latestHumidityValue) : tokens.color.white}
        lineGradientStartColor={
          latestHumidityValue ? getHumidityGradient(latestHumidityValue).start : tokens.color.white
        }
        startFillColor={
          latestHumidityValue ? getHumidityGradient(latestHumidityValue).start : tokens.color.white
        }
        lineGradientEndColor={
          latestHumidityValue ? getHumidityGradient(latestHumidityValue).end : tokens.color.white
        }
        endFillColor={
          latestHumidityValue ? getHumidityGradient(latestHumidityValue).end : tokens.color.white
        }
      />
    </YStack>
  );
};
